import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { VideoBuyShell } from "./VideoBuyShell";
import { VideoBuyStage } from "./VideoBuyStage";
import { CheckoutSheet } from "./CheckoutSheet";
import { CardReturn } from "./CardReturn";

// Funil no modo "Vídeo + Compra direta": vídeo → botão → nome/e-mail → Pix/Cartão → redireciona
export const DirectBuyFunnel = ({ funnel, slug, refCode }) => {
  const [sp, setSp] = useSearchParams();
  const [planIdx, setPlanIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const leadRef = useRef(null);
  const plans = funnel.plans || [];
  const plan = plans[planIdx] || { name: funnel.title, price: 0 };
  const returnTx = sp.get("tx");

  useEffect(() => {
    if (sp.get("canceled")) {
      toast.info("Pagamento cancelado. Você pode tentar novamente quando quiser.", { id: "pay-canceled" });
      setSp({}, { replace: true });
    }
  }, [sp, setSp]);

  const pollStatus = useCallback((tx) => api.get(`/public/funnels/${slug}/status/${tx}`).then((r) => r.data), [slug]);

  const onStart = useCallback(async ({ name, email, method }) => {
    let leadId = leadRef.current?.email === email ? leadRef.current.id : null;
    if (!leadId) {
      const { data } = await api.post(`/public/funnels/${slug}/lead`, { name, email, ref: refCode });
      leadId = data.lead_id;
      leadRef.current = { email, id: leadId };
    }
    if (method === "card") {
      const { data } = await api.post(`/public/funnels/${slug}/pay-card`, { lead_id: leadId, plan_index: planIdx, origin_url: window.location.origin });
      return data;
    }
    const { data } = await api.post(`/public/funnels/${slug}/pay`, { lead_id: leadId, plan_index: planIdx });
    if (data.free) return { ...(await pollStatus(data.tx_id)), buyer_name: name };
    return data;
  }, [slug, refCode, planIdx, pollStatus]);

  const onMockConfirm = useCallback(async (tx) => {
    await api.post(`/public/funnels/${slug}/confirm/${tx}`);
    return pollStatus(tx);
  }, [slug, pollStatus]);

  const data = {
    title: funnel.title,
    headline: funnel.direct_buy_headline,
    video_url: funnel.direct_buy_video_url || funnel.video_url,
    price: plan.price,
    button_text: funnel.direct_buy_button_text,
    require_watch: !!funnel.direct_buy_require_watch,
    require_watch_percent: funnel.require_watch_percent ?? 95,
    accept_pix: true,
    accept_card: funnel.direct_buy_accept_card !== false && Number(plan.price) >= 1,
  };

  return (
    <VideoBuyShell ownerName={funnel.owner_name}>
      <VideoBuyStage data={data} plans={plans} selectedPlan={planIdx} onSelectPlan={setPlanIdx} onBuy={() => setOpen(true)} />
      <CheckoutSheet
        open={open}
        onClose={() => setOpen(false)}
        productName={plans.length > 1 ? `${funnel.title} · ${plan.name}` : funnel.title}
        price={plan.price}
        acceptPix
        acceptCard={data.accept_card}
        onStart={onStart}
        pollStatus={pollStatus}
        onMockConfirm={onMockConfirm}
      />
      {returnTx && <CardReturn txId={returnTx} pollStatus={pollStatus} onDismiss={() => setSp({}, { replace: true })} />}
    </VideoBuyShell>
  );
};
